// Every old Analytics address lands on its tab in the home, with the site carried over. The home resolves
// whatever id the old link had (Xano id, legacy:site:N, replay id, journey id, business profile) to the
// website, so a link only needs to say which param held it. The screen's other params ride along.
import { Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { isInsightsTab } from "@/lib/insights";
import type { TabId } from "./model";

/** Where an old address goes: its tab, sub-view, and the param that named the site (moved to ?site=). */
export function analyticsRedirect(tab: TabId, search: string, opts: { sub?: string; siteFrom?: string; site?: string | null; drop?: string[] } = {}): string {
  const q = new URLSearchParams(search);
  const site = opts.site ?? (opts.siteFrom ? q.get(opts.siteFrom) : null);
  if (opts.siteFrom) q.delete(opts.siteFrom);
  for (const k of opts.drop ?? []) q.delete(k);
  q.delete("tab"); q.delete("sub"); q.delete("site");
  const head = new URLSearchParams();
  if (tab !== "overview") head.set("tab", tab);
  if (opts.sub) head.set("sub", opts.sub);
  if (site && site !== "all") head.set("site", site);
  for (const [k, v] of q) head.append(k, v);
  const s = head.toString();
  return s ? `/analytics?${s}` : "/analytics";
}

export function ToAnalytics({ tab, sub, siteFrom, drop }: { tab: TabId; sub?: string; siteFrom?: string; drop?: string[] }) {
  const { search } = useLocation();
  return <Navigate replace to={analyticsRedirect(tab, search, { sub, siteFrom, drop })} />;
}

/**
 * /insights, /insights/:tab and /insights/:tab/:siteId. Search Console, Google Analytics, Assistants and bots
 * are tabs of the home now; Audit takes a URL rather than a site and keeps its page (`keep`).
 */
export function insightsRedirect(tab: string | undefined, siteId: string | undefined, search: string): string | null {
  const q = new URLSearchParams(search);
  const native = q.get("native_site");
  const numeric = (v: string | undefined) => (v && /^[1-9]\d*$/.test(v) ? v : null);
  const site = native && native !== "all" ? native : numeric(siteId);
  const drop = ["native_site", "view", "node"];
  // A numeric tab is a pre-regroup /insights/12 link: that site's bots.
  if (numeric(tab)) return analyticsRedirect("traffic", search, { sub: "bots", site: numeric(tab), drop });
  if (tab === "visibility") return analyticsRedirect("ai", search, { site, drop });
  if (tab === "search") return analyticsRedirect("search", search, { site, drop });
  if (tab === "assistants") return analyticsRedirect("ai", search, { sub: "assistants", site, drop });
  if (tab === "audience") {
    if (q.get("view") === "experiments") return analyticsRedirect("experiments", search, { site, drop });
    return analyticsRedirect("traffic", search, { sub: q.get("view") === "ga4" ? "ga4" : undefined, site, drop });
  }
  if (tab === "audit") return null;
  if (!tab) return analyticsRedirect("overview", search, { site, drop });
  if (!isInsightsTab(tab) || tab === "bots") return analyticsRedirect("traffic", search, { sub: "bots", site, drop });
  return null;
}

export function InsightsRedirect({ keep }: { keep: JSX.Element }) {
  const { tab, siteId } = useParams<{ tab?: string; siteId?: string }>();
  const [search] = useSearchParams();
  const to = insightsRedirect(tab, siteId, search.toString());
  return to ? <Navigate replace to={to} /> : keep;
}
